import { wupoServerUri, federationApiBase } from '../../Api';

/**
 * Thin client for the cross-cloud client-data federation surface
 * (backend ManagementController + FetchController at
 * `/api/secure/federation/**`, Phase 5). Mirrors the fetch/auth/error
 * conventions of chatApi.js: one JWT bearer header builder, a defensive
 * JSON parser, and inline path suffixes composed off the base in Api.js.
 *
 * NO CLOUD CREDENTIALS are ever sent or received here. Onboarding hands the
 * client IaC (CloudFormation / Terraform / Azure script or ARM) they run in THEIR
 * cloud; on apply it calls our callback with a single-use onboarding token, and the
 * browser only polls state. If that call cannot reach us, an operator binds the
 * principal by hand via verifyTrust (FEDERATION_MANAGE).
 */

const base = () => `${wupoServerUri.devnet}${federationApiBase}`;

const jsonHeaders = (jwtToken) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${jwtToken}`,
});

const safeJson = async (response) => {
  if (!response || !response.ok) return null;
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

// ── Enum value sets (authoritative in federation-api-contract.md) ────────────
export const CLOUDS = ['AWS', 'GCP', 'AZURE'];
export const ONBOARDING_STATES = [
  'DRAFT',
  'TEMPLATE_ISSUED',
  'TRUST_VERIFIED',
  'VALIDATED',
  'SCHEDULED',
  'ACTIVE',
  'DEGRADED',
  'BROKEN',
];
export const FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'EVERY_N_DAYS'];
// V1 data formats & layouts.
export const SNAPSHOT_MODES = ['ALL', 'LATEST'];
export const SOURCE_FORMATS = ['PARQUET', 'DELTA', 'CSV', 'JSON'];
export const REJECT_REASONS = [
  'ICEBERG',
  'HUDI',
  'EXCEL',
  'ARCHIVE',
  'ORC',
  'AVRO',
  'MIXED_FORMATS',
  'DELTA_LOG_INCOMPLETE',
  'UNSAFE_PATH',
];
export const LAYOUT_WARNING_CODES = [
  'NO_SUPPORTED_DATA',
  'UNRECOGNIZED_FILES',
  'INCONSISTENT_PARTITIONS',
  'UNSAFE_PARTITION_PATH',
  'UNDATED_FILES_DROPPED',
  'SIDECAR_FILES_IGNORED',
  'TABLE_NAME_COLLISION',
  'DMS_CDC_FILES_IGNORED',
  'SCHEMA_CHANGED',
];
/** Connection lastRunStatus values (null before the first run). */
export const LAST_RUN_STATUSES = ['SUCCESS', 'EMPTY', 'FAILED'];

// ── Read helpers (no user-facing copy; components translate labels) ──────────

/** MUI Chip color for an OnboardingState. */
export const stateChipColor = (state) => {
  switch (state) {
    case 'ACTIVE':
      return 'success';
    case 'SCHEDULED':
      return 'primary';
    case 'TEMPLATE_ISSUED':
    case 'TRUST_VERIFIED':
    case 'VALIDATED':
      return 'info';
    case 'DEGRADED':
      return 'warning';
    case 'BROKEN':
      return 'error';
    case 'DRAFT':
    default:
      return 'default';
  }
};

/** MUI Chip color for a run status (FetchRun.Status or lastRunStatus). */
export const runStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED':
    case 'SUCCESS':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'EMPTY':
      return 'warning'; // run completed but no supported dataset was found
    case 'RUNNING':
    case 'CLAIMED':
      return 'info';
    case 'PENDING':
    default:
      return 'default';
  }
};

/**
 * Format a snapshot token (the digits `yyyy[MM[dd[HH[mm[ss]]]]]` of a dated folder / file
 * name, as in `TableDto.latestSnapshot`) as `yyyy[-MM[-dd[ HH:mm]]]`. Seconds are dropped;
 * an unexpected value is returned unchanged.
 */
export const formatSnapshotToken = (token) => {
  if (!token || !/^\d{4,}$/.test(token)) return token || '';
  const [yyyy, mm, dd, hh, mi] = [0, 4, 6, 8, 10].map((i) => token.substring(i, i + (i === 0 ? 4 : 2)));
  let out = yyyy;
  if (mm) out += `-${mm}`;
  if (mm && dd) out += `-${dd}`;
  if (mm && dd && hh) out += ` ${hh}:${mi || '00'}`;
  return out;
};

/**
 * Parse a FetchRunSummaryDto.outcomeJson string. Returns the object, or null when it is
 * absent / not JSON. An ingest outcome carries `tables[]` (empty = EMPTY run),
 * `rejected[]` and `warnings[]`; error / health-check outcomes carry other keys.
 */
export const parseRunOutcome = (outcomeJson) => {
  if (!outcomeJson) return null;
  try {
    const parsed = JSON.parse(outcomeJson);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

/** A BROKEN connection must be re-onboarded (guide back to the template step). */
export const isBroken = (connection) => connection && connection.onboardingState === 'BROKEN';

/**
 * The wizard's earliest resumable step for a connection already past DRAFT — so
 * reopening a half-onboarded (or BROKEN) connection lands on the right step.
 * Steps are 0-indexed: 0=source, 1=template, 2=validate, 3=schedule.
 */
export const wizardStepForState = (state) => {
  switch (state) {
    case 'DRAFT':
      return 0;
    case 'TEMPLATE_ISSUED':
      return 1; // waiting on external callback → TRUST_VERIFIED
    case 'TRUST_VERIFIED':
      return 2; // ready to validate
    case 'VALIDATED':
    case 'SCHEDULED':
    case 'ACTIVE':
    case 'DEGRADED':
      return 3; // schedule
    case 'BROKEN':
      return 1; // re-onboard: re-issue the template
    default:
      return 0;
  }
};

// ── Endpoints ────────────────────────────────────────────────────────────────

/** GET /connections — ConnectionSummaryDto[] for the caller's tenant. */
export const listConnections = async (jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('listConnections error:', e);
    return null;
  }
};

/** GET /connections/{id} — ConnectionDetailDto (incl. recentRuns, schedule). */
export const getConnection = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('getConnection error:', e);
    return null;
  }
};

/**
 * POST /connections — creates the DRAFT connection. Body: CreateConnectionRequest.
 * Returns `{ ok, data, error }` so the wizard can show the backend's 400 reason (e.g. an
 * Azure storage account in another Entra tenant, an invalid account name).
 */
export const createConnection = async (request, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      return { ok: false, data: null, error: await errorMessage(response), status: response.status };
    }
    const data = await safeJson(response);
    return data ? { ok: true, data, error: null } : { ok: false, data: null, error: null, status: response.status };
  } catch (e) {
    console.error('createConnection error:', e);
    return { ok: false, data: null, error: null, status: null };
  }
};

/** POST /connections/{id}/onboarding/template — OnboardingTemplateDto (DRAFT→TEMPLATE_ISSUED). */
export const issueTemplate = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/onboarding/template`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('issueTemplate error:', e);
    return null;
  }
};

/**
 * Best-effort error message from a non-OK response: the JSON body's `message` string
 * when present, otherwise the raw text body (trimmed, capped), otherwise null.
 */
const errorMessage = async (response) => {
  if (!response) return null;
  let text = '';
  try {
    text = await response.text();
  } catch (e) {
    return null;
  }
  if (!text || !text.trim()) return null;
  try {
    const body = JSON.parse(text);
    if (body && typeof body === 'object') {
      return typeof body.message === 'string' && body.message.trim() ? body.message.trim() : null;
    }
  } catch (e) {
    // not JSON — fall through to the text body
  }
  const trimmed = text.trim();
  // An HTML error page is not a useful message.
  if (trimmed.startsWith('<')) return null;
  return trimmed.length > 500 ? `${trimmed.substring(0, 500)}…` : trimmed;
};

/**
 * POST /connections/{id}/onboarding/verify-trust — manual trust bind (operator fallback when
 * the client's IaC cannot reach the callback). Body: VerifyTrustRequest
 * `{ roleArn?, serviceAccount?, principal?, projectNumber?, workloadIdentityProvider? }`.
 * Returns `{ ok, data: ConnectionDetailDto, error }`.
 */
export const verifyTrust = async (id, body, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/onboarding/verify-trust`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify(body || {}),
    });
    if (!response.ok) {
      return { ok: false, data: null, error: await errorMessage(response), status: response.status };
    }
    const data = await safeJson(response);
    return data ? { ok: true, data, error: null } : { ok: false, data: null, error: null, status: response.status };
  } catch (e) {
    console.error('verifyTrust error:', e);
    return { ok: false, data: null, error: null, status: null };
  }
};

/**
 * POST /connections/{id}/onboarding/validate — ValidationResultDto (TRUST_VERIFIED→VALIDATED).
 *
 * Unlike the other helpers this returns `{ ok, data, error }` so the wizard can show the
 * backend's reason on failure (e.g. a 409 naming the unreadable / wrong-format object key):
 *  - success:  `{ ok: true, data: ValidationResultDto, error: null }`
 *  - failure:  `{ ok: false, data: null, error: <backend message> | null, status }`
 */
export const validateConnection = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/onboarding/validate`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    if (!response.ok) {
      return { ok: false, data: null, error: await errorMessage(response), status: response.status };
    }
    const data = await safeJson(response);
    return data ? { ok: true, data, error: null } : { ok: false, data: null, error: null, status: response.status };
  } catch (e) {
    console.error('validateConnection error:', e);
    return { ok: false, data: null, error: null, status: null };
  }
};

/**
 * PUT /connections/{id}/data-options — body `{ snapshotMode?, csvHeader? }` (null/missing =
 * unchanged) → ConnectionDetailDto. Allowed in any state, no state change; applies to the
 * next probe / fetch.
 */
export const updateDataOptions = async (id, { snapshotMode, csvHeader } = {}, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/data-options`, {
      method: 'PUT',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify({
        snapshotMode: snapshotMode ?? null,
        csvHeader: csvHeader ?? null,
      }),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('updateDataOptions error:', e);
    return null;
  }
};

/** GET /connections/{id}/schedule — ScheduleSpecDto (with nextRunAt/nextRunPreview). */
export const getSchedule = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/schedule`, {
      method: 'GET',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('getSchedule error:', e);
    return null;
  }
};

/**
 * PUT /connections/{id}/schedule — ScheduleSpecDto in, ScheduleSpecDto out.
 * A valid enabled schedule advances VALIDATED/SCHEDULED → SCHEDULED and
 * enqueues the ACTIVATION fetch (worker success then promotes to ACTIVE).
 */
export const updateSchedule = async (id, spec, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/schedule`, {
      method: 'PUT',
      headers: jsonHeaders(jwtToken),
      body: JSON.stringify(spec),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('updateSchedule error:', e);
    return null;
  }
};

/** POST /connections/{id}/schedule/pause — ScheduleSpecDto (enabled=false). */
export const pauseSchedule = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/schedule/pause`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('pauseSchedule error:', e);
    return null;
  }
};

/** POST /connections/{id}/schedule/resume — ScheduleSpecDto (enabled=true, recomputed). */
export const resumeSchedule = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/schedule/resume`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('resumeSchedule error:', e);
    return null;
  }
};

/** DELETE /connections/{id} — 204 on success (tenant-scoped). Returns true/false. */
export const deleteConnection = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}`, {
      method: 'DELETE',
      headers: jsonHeaders(jwtToken),
    });
    return !!(response && (response.status === 204 || response.ok));
  } catch (e) {
    console.error('deleteConnection error:', e);
    return false;
  }
};

/**
 * POST /connections/{id}/fetch-now — 202 with { runId, connectionId, status, trigger }
 * (new or de-duped in-flight run). Authority FEDERATION_FETCH.
 */
export const fetchNow = async (id, jwtToken) => {
  try {
    const response = await fetch(`${base()}/connections/${id}/fetch-now`, {
      method: 'POST',
      headers: jsonHeaders(jwtToken),
    });
    return await safeJson(response);
  } catch (e) {
    console.error('fetchNow error:', e);
    return null;
  }
};
